import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const SelectSearch = ({
  name,
  change,
  label,
  datas = [],
  dataIndex = "id",
  dataLabel = "name",
  dataLabel2 = null,
  value = "",
  placeholder = "Rechercher...",
  maxSuggestions = 30,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const getField = (item, path) => {
    if (!item || !path) return "";
    return path.split(".").reduce((acc, k) => (acc ? acc[k] : ""), item) || "";
  };

  const selectedItem = datas.find(
    (item) => String(item[dataIndex]) === String(value),
  );

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(datas.slice(0, maxSuggestions));
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = datas
      .filter((item) => {
        const t1 = String(getField(item, dataLabel)).toLowerCase();
        const t2 = dataLabel2
          ? String(getField(item, dataLabel2)).toLowerCase()
          : "";
        return t1.includes(term) || t2.includes(term);
      })
      .slice(0, maxSuggestions);
    setFilteredData(filtered);
  }, [searchTerm, datas, dataLabel, dataLabel2, maxSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (item) => {
    change({ target: { name, value: item[dataIndex] } });
    setSearchTerm("");
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    change({ target: { name, value: "" } });
    setSearchTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const displayValue = isOpen
    ? searchTerm
    : selectedItem
      ? getField(selectedItem, dataLabel)
      : searchTerm;

  return (
    <div className="form-group" ref={wrapperRef}>
      {label && <label htmlFor={name}>{label}</label>}

      <div className="position-relative">
        <div
          className="form-control d-flex align-items-center"
          style={{ minHeight: 38, cursor: "text", paddingRight: 30 }}
          onClick={() => inputRef.current && inputRef.current.focus()}
        >
          <input
            ref={inputRef}
            type="text"
            className="border-0 flex-grow-1 p-0"
            style={{ minWidth: 0, outline: "none", background: "transparent" }}
            value={displayValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedItem ? "" : placeholder}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={`${name}-dropdown`}
          />
          {(selectedItem || searchTerm) && (
            <button
              type="button"
              className="btn btn-link p-0 position-absolute border-0"
              style={{ right: 8, top: "50%", transform: "translateY(-50%)" }}
              onClick={handleClear}
              aria-label="Effacer"
            >
              <i className="fa fa-times text-muted" />
            </button>
          )}
        </div>

        {isOpen && filteredData.length > 0 && (
          <div
            className="dropdown-menu show w-100"
            id={`${name}-dropdown`}
            style={{ maxHeight: 250, overflowY: "auto", zIndex: 1060 }}
            role="listbox"
          >
            {filteredData.map((item, index) => {
              const isSelected = String(item[dataIndex]) === String(value);
              return (
                <button
                  key={`${item[dataIndex]}-${index}`}
                  type="button"
                  className={`dropdown-item ${isSelected ? "active" : ""}`}
                  onClick={() => handleSelect(item)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span>{getField(item, dataLabel)}</span>
                  {dataLabel2 && (
                    <small className="text-muted ml-2">
                      {getField(item, dataLabel2)}
                    </small>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {isOpen && filteredData.length === 0 && (
          <div
            className="dropdown-menu show w-100 px-3 py-2 text-muted"
            style={{ zIndex: 1060 }}
          >
            Aucun résultat
          </div>
        )}
      </div>
    </div>
  );
};

SelectSearch.propTypes = {
  name: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
  label: PropTypes.string,
  datas: PropTypes.arrayOf(PropTypes.object),
  dataIndex: PropTypes.string,
  dataLabel: PropTypes.string,
  dataLabel2: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  maxSuggestions: PropTypes.number,
};

export default SelectSearch;
